/** Simple prev/next pagination footer with an optional "All" toggle.
 *
 * Kept dumb — the parent owns the current page, page size, and total
 * count and slices the data itself. This component only renders the
 * controls and calls `onPageChange(nextPage)` / `onSizeChange(size)`. */

import type { ReactElement } from "react";

/** "all" collapses the list to a single page showing everything. */
export type PageSize = number | "all";

/** Rows per page when "All" is off. */
const DEFAULT_NUMERIC_SIZE = 5;

/** Resolve a PageSize to the concrete row count used for slicing. */
export function effectivePageSize(size: PageSize, totalItems: number): number {
  return size === "all" ? Math.max(totalItems, 1) : size;
}

interface Props {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Optional noun for the "Showing X-Y of Z" text (e.g. "trades",
   *  "alerts"). Defaults to "items". */
  itemNoun?: string;
  /** When provided (with `sizeValue`), renders the "All" toggle button.
   * The parent resets the page to 1 on change. */
  sizeValue?: PageSize;
  onSizeChange?: (size: PageSize) => void;
}

export function Pagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  itemNoun = "items",
  sizeValue,
  onSizeChange,
}: Readonly<Props>): ReactElement | null {
  const hasAllToggle = onSizeChange !== undefined && sizeValue !== undefined;
  // Without the toggle: footer is pure prev/next chrome, useless on one
  // page. With it: keep the footer visible whenever All would change
  // what's shown (i.e. more items than one default page).
  if (hasAllToggle ? totalItems <= DEFAULT_NUMERIC_SIZE : totalItems <= pageSize) return null;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);
  return (
    <div className="flex items-center justify-between border-t border-line px-4 py-2 text-xs text-ink-secondary">
      <span>
        Showing {String(start)}-{String(end)} of {String(totalItems)} {itemNoun}
      </span>
      <div className="flex items-center gap-3">
        {hasAllToggle && <ShowAll active={sizeValue === "all"} onChange={onSizeChange} />}
        <NavButton
          label="Prev"
          icon="ti-chevron-left"
          disabled={currentPage <= 1}
          onClick={() => {
            onPageChange(currentPage - 1);
          }}
        />
        <span>
          Page <span className="font-medium text-ink">{String(currentPage)}</span> of{" "}
          {String(totalPages)}
        </span>
        <NavButton
          label="Next"
          icon="ti-chevron-right"
          iconAfter
          disabled={currentPage >= totalPages}
          onClick={() => {
            onPageChange(currentPage + 1);
          }}
        />
      </div>
    </div>
  );
}

function ShowAll({
  active,
  onChange,
}: Readonly<{ active: boolean; onChange: (size: PageSize) => void }>): ReactElement {
  return (
    <span className="flex items-center gap-1.5">
      Show
      <AllButton active={active} onChange={onChange} />
    </span>
  );
}

/** On = everything on one page; off = back to the default page size. */
function AllButton({
  active,
  onChange,
}: Readonly<{ active: boolean; onChange: (size: PageSize) => void }>): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`rounded border px-2 py-1 text-xs font-medium ${
        active
          ? "border-accent-border bg-accent-bg text-accent"
          : "border-line bg-surface-2 text-ink-secondary hover:bg-surface-1"
      }`}
      onClick={() => {
        onChange(active ? DEFAULT_NUMERIC_SIZE : "all");
      }}
    >
      All
    </button>
  );
}

function NavButton({
  label,
  icon,
  iconAfter = false,
  disabled,
  onClick,
}: Readonly<{
  label: string;
  icon: string;
  iconAfter?: boolean;
  disabled: boolean;
  onClick: () => void;
}>): ReactElement {
  return (
    <button
      type="button"
      className="btn btn-secondary px-2 py-1 text-xs disabled:opacity-40"
      onClick={onClick}
      disabled={disabled}
    >
      {!iconAfter && <i className={`ti ${icon} text-xs`} />}
      {label}
      {iconAfter && <i className={`ti ${icon} text-xs`} />}
    </button>
  );
}

/** Convenience: slice the current page from a full array. */
export function pageSlice<T>(items: readonly T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
